// frontend/src/components/ui/FormattedInput.tsx
// ─────────────────────────────────────────────────────────────────────────────
// Eingabefeld mit LIVE-Formatierung direkt im Feld: *fett* _kursiv_
// ~durchgestrichen~ `code` werden sofort formatiert dargestellt, die Marker-
// Zeichen bleiben sichtbar, aber ausgegraut. KEIN zweites Vorschau-Feld.
//
// Technik: ein contenteditable-<div>. Der Text bleibt React-kontrolliert (value
// als Quelle der Wahrheit); der DOM-Inhalt wird imperativ aus dem zentralen
// Markdown-Tokenizer gesetzt (markdownToHtmlWithMarkers). Caret-Erhalt über
// einen kurzlebigen PUA-Marker (), der in den Plaintext serialisiert,
// gesucht und wieder entfernt wird → robust gegen die <div>/<br>-Eigenheiten
// der Browser. IME/Autocomplete: während der Komposition wird NICHT neu
// gehighlightet (sonst bricht die Eingabe ab).
import {
  forwardRef,
  useCallback,
  useEffect,
  useImperativeHandle,
  useLayoutEffect,
  useRef,
  type KeyboardEvent as ReactKeyboardEvent,
} from 'react';
import { markdownToHtmlWithMarkers } from '../../lib/markdown';
import styles from './FormattedInput.module.css';
import { cx } from '../chat/lib/classNames';

const CARET_MARKER = '';

export interface FormattedInputHandle {
  focus: () => void;
  // E4a: KEINE blur()-Methode. Fokus wird immer auf ein Ziel gesetzt, nie
  // ent-fokussiert (Apple-Forum 814432: Ctrl+Home/End nach blur() kann via
  // UIKit-Bug abstuerzen). Kein Aufrufer brauchte sie — entfernt.
}

export interface FormattedInputProps {
  value: string;
  onChange: (value: string) => void;
  placeholder?: string;
  /**
   * R4-01: Mehrzeiligkeit im Sinne der DATEN — steuert, ob Enter einen echten
   * Zeilenumbruch (\n) in den Wert schreibt und ob das Feld semantisch
   * mehrzeilig ist (`aria-multiline`). Default true.
   *
   * Bewusst GETRENNT von `wrap` (Umbruch beim Anzeigen): der Begleiter-Bogen
   * braucht Felder, die optisch mitwachsen, deren Wert aber garantiert
   * einzeilig bleibt — ein \n in „Kleidungsstil" erzeugt in
   * server/companion-character.js formatCompanionDataForPrompt eine erfundene
   * zusaetzliche Feldzeile, die auch in die Bild- und Status-Prompts laeuft.
   */
  multiline?: boolean;
  /**
   * R4-01: Umbruch beim ANZEIGEN. Default = `multiline`. Mit
   * `multiline={false} wrap` waechst das Feld mit dem Text, nimmt aber keinen
   * Zeilenumbruch in den Wert auf (Julius: „einzeilig, waechst automatisch").
   */
  wrap?: boolean;
  maxLength?: number;
  disabled?: boolean;
  className?: string;
  ariaLabel?: string;
  /** R4-01: aria-describedby des umgebenden FormField (Hilfe-/Fehlertext). */
  ariaDescribedBy?: string;
  ariaInvalid?: boolean;
  id?: string;
  rows?: number;
  onKeyDown?: (event: ReactKeyboardEvent<HTMLDivElement>) => void;
  /**
   * R4-01: Ausgang aus dem Enter-Riegel. Bei `multiline={false}` wehrt das Feld
   * den Umbruch selbst mit preventDefault() ab — ein Formular-Handler weiter
   * oben steigt danach bei `event.defaultPrevented` aus. `onEnter` wird
   * deshalb NACH dem preventDefault() gerufen und traegt den Enter-Vertrag
   * (im Begleiter-Bogen: „Weiter") weiter.
   */
  onEnter?: (event: ReactKeyboardEvent<HTMLDivElement>) => void;
  onFocus?: () => void;
  onBlur?: () => void;
}

// DOM → Plaintext (browser fügt beim Enter <div>/<br> ein; wir normalisieren).
// Nimmt auch ein DocumentFragment (Caret-Präfix, siehe readTextAndCaret).
function domToText(root: Node): string {
  let out = '';
  const walk = (node: Node) => {
    node.childNodes.forEach((child) => {
      if (child.nodeType === Node.TEXT_NODE) {
        out += child.textContent ?? '';
      } else if (child.nodeName === 'BR') {
        out += '\n';
      } else if (child.nodeType === Node.ELEMENT_NODE) {
        const isBlock = /^(DIV|P)$/.test(child.nodeName);
        if (isBlock && out.length > 0 && !out.endsWith('\n')) out += '\n';
        walk(child);
      }
    });
  };
  walk(root);
  return out;
}

// Liest Plaintext + Caret-Offset.
// S-17 (7.4): OHNE den DOM anzufassen — der Caret-Offset ist die Textlänge des
// Präfixes vom Feldanfang bis zur Caret-Position (Range.cloneContents), mit
// derselben Normalisierung wie der Gesamttext. Früher wurde ein Marker-Knoten
// eingefügt und wieder entfernt; das zerteilte Textknoten und war nur deshalb
// unschädlich, weil danach ohnehin der ganze Inhalt neu geschrieben wurde. Seit
// handleInput unveränderte Formatierung nicht mehr neu schreibt, muss das
// Lesen den DOM unberührt lassen (Caret, Rechtschreibprüfung, Autokorrektur).
// Fallback auf den alten Marker-Weg, falls cloneContents fehlt/wirft.
function readTextAndCaret(root: HTMLElement): { text: string; caret: number } {
  const text = domToText(root);
  const sel = window.getSelection();
  if (!sel || sel.rangeCount === 0) return { text, caret: text.length };
  const selection = sel.getRangeAt(0);
  if (!root.contains(selection.endContainer)) return { text, caret: text.length };
  try {
    const prefix = document.createRange();
    prefix.setStart(root, 0);
    prefix.setEnd(selection.endContainer, selection.endOffset);
    const caret = domToText(prefix.cloneContents()).length;
    return { text, caret: Math.min(caret, text.length) };
  } catch {
    return readTextAndCaretWithMarker(root);
  }
}

function readTextAndCaretWithMarker(root: HTMLElement): { text: string; caret: number } {
  const sel = window.getSelection();
  let markerNode: Text | null = null;
  if (sel && sel.rangeCount > 0 && root.contains(sel.getRangeAt(0).endContainer)) {
    const range = sel.getRangeAt(0).cloneRange();
    range.collapse(false);
    markerNode = document.createTextNode(CARET_MARKER);
    try { range.insertNode(markerNode); } catch { markerNode = null; }
  }
  let full = domToText(root);
  const parent = markerNode?.parentNode ?? null;
  markerNode?.parentNode?.removeChild(markerNode);
  // Zerteilte Textknoten wieder zusammenführen (Live-Ranges bleiben erhalten).
  try { parent?.normalize(); } catch { /* best-effort */ }
  const caret = full.indexOf(CARET_MARKER);
  full = full.replace(CARET_MARKER, '');
  return { text: full, caret: caret >= 0 ? caret : full.length };
}

// Serialisierter DOM-Inhalt so normalisiert, dass er mit markdownToHtmlWithMarkers
// vergleichbar ist: innerHTML schreibt ein geschütztes Leerzeichen als &nbsp;,
// der Tokenizer lässt das Zeichen stehen.
function serializedFieldHtml(el: HTMLElement): string {
  return el.innerHTML.replace(/&nbsp;/g, '\u00a0');
}

// Caret an Zeichen-Offset setzen (DOM ist nach dem Re-Highlight nur Text + <br>
// in <strong>/<em>/<s>/<code>/<span>).
function setCaret(root: HTMLElement, offset: number): void {
  const sel = window.getSelection();
  if (!sel) return;
  const range = document.createRange();
  let remaining = Math.max(0, offset);
  let placed = false;

  const walk = (node: Node): boolean => {
    for (const child of Array.from(node.childNodes)) {
      if (placed) return true;
      if (child.nodeType === Node.TEXT_NODE) {
        const len = (child.textContent ?? '').length;
        if (remaining <= len) { range.setStart(child, remaining); placed = true; return true; }
        remaining -= len;
      } else if (child.nodeName === 'BR') {
        if (remaining === 0) { range.setStartBefore(child); placed = true; return true; }
        remaining -= 1;
      } else if (child.nodeType === Node.ELEMENT_NODE) {
        if (walk(child)) return true;
      }
    }
    return false;
  };

  walk(root);
  if (!placed) {
    range.selectNodeContents(root);
    range.collapse(false);
  } else {
    range.collapse(true);
  }
  sel.removeAllRanges();
  sel.addRange(range);
}

export const FormattedInput = forwardRef<FormattedInputHandle, FormattedInputProps>(function FormattedInput(
  {
    value,
    onChange,
    placeholder,
    multiline = true,
    wrap,
    maxLength,
    disabled,
    className,
    ariaLabel,
    ariaDescribedBy,
    ariaInvalid,
    id,
    rows,
    onKeyDown,
    onEnter,
    onFocus,
    onBlur,
  },
  ref,
) {
  // Umbruch folgt der Mehrzeiligkeit, solange nichts anderes gesagt ist.
  const wrapping = wrap ?? multiline;
  const elRef = useRef<HTMLDivElement>(null);
  const composingRef = useRef(false);
  // Letzter Wert, der von UNS in den DOM geschrieben wurde — verhindert
  // unnötige Re-Renders (und Caret-Sprünge), wenn value === DOM-Text.
  const lastWrittenRef = useRef<string | null>(null);

  useImperativeHandle(ref, () => ({
    focus: () => elRef.current?.focus(),
  }), []);

  const paint = useCallback((text: string, caret: number | null, html?: string) => {
    const el = elRef.current;
    if (!el) return;
    el.innerHTML = html ?? markdownToHtmlWithMarkers(text);
    el.dataset.empty = text.length === 0 ? 'true' : 'false';
    lastWrittenRef.current = text;
    if (caret !== null) setCaret(el, caret);
  }, []);

  // Externe value-Änderung (z.B. Composer leert nach dem Senden) in den DOM
  // spiegeln — nur wenn sie nicht von unserem eigenen onChange stammt.
  useLayoutEffect(() => {
    if (value === lastWrittenRef.current) return;
    const el = elRef.current;
    if (!el) return;
    const active = document.activeElement === el;
    paint(value, active ? value.length : null);
  }, [value, paint]);

  const handleInput = useCallback(() => {
    const el = elRef.current;
    if (!el || composingRef.current) return;
    let { text, caret } = readTextAndCaret(el);
    // R4-01: Einzeilig im Sinne der Daten. Enter ist zwar gesperrt, ein
    // eingefuegter Text kann aber Umbrueche mitbringen — die werden hier
    // LAENGENTREU durch ein Leerzeichen ersetzt, damit der Karett-Offset
    // stimmt und im Wert garantiert kein \n landet.
    if (!multiline) text = text.replace(/[\r\n]/g, ' ');
    if (typeof maxLength === 'number' && text.length > maxLength) {
      text = text.slice(0, maxLength);
      caret = Math.min(caret, maxLength);
    }
    // S-17 (7.4): nur neu schreiben, wenn sich die Darstellung wirklich ändert
    // (Formatmarker hinzugekommen/weggefallen, Umbruch normalisiert, Kürzung).
    // Beim normalen Tippen steht im DOM schon genau das, was der Tokenizer
    // erzeugen würde — dann bleiben Knoten und Caret, wie der Browser sie hat.
    const html = markdownToHtmlWithMarkers(text);
    if (serializedFieldHtml(el) === html) {
      el.dataset.empty = text.length === 0 ? 'true' : 'false';
      lastWrittenRef.current = text;
    } else {
      paint(text, caret, html);
    }
    if (text !== value) onChange(text);
  }, [maxLength, multiline, onChange, paint, value]);

  const handleComposition = useCallback((end: boolean) => {
    composingRef.current = !end;
    if (end) handleInput();
  }, [handleInput]);

  // Paste IMMER als Plaintext (kein Fremd-HTML in das Feld lassen).
  const handlePaste = useCallback((event: React.ClipboardEvent<HTMLDivElement>) => {
    event.preventDefault();
    const clip = event.clipboardData?.getData('text/plain') ?? '';
    if (!clip) return;
    const sel = window.getSelection();
    if (sel && sel.rangeCount > 0) {
      const range = sel.getRangeAt(0);
      range.deleteContents();
      range.insertNode(document.createTextNode(clip));
      range.collapse(false);
      sel.removeAllRanges();
      sel.addRange(range);
    }
    handleInput();
  }, [handleInput]);

  const handleKeyDown = useCallback((event: ReactKeyboardEvent<HTMLDivElement>) => {
    onKeyDown?.(event);
    if (event.defaultPrevented) return;
    if (event.key !== 'Enter' || multiline) return;
    // IME: waehrend einer laufenden Komposition bestaetigt Enter den Vorschlag
    // — weder sperren noch als „Weiter" deuten.
    if (event.nativeEvent.isComposing) return;
    // Einzeilig: kein Umbruch — auch NICHT mit Shift (R4-01: ein \n zerlegt
    // sonst still den Begleiter-Prompt).
    event.preventDefault();
    if (event.shiftKey || event.ctrlKey || event.metaKey || event.altKey) return;
    onEnter?.(event);
  }, [multiline, onEnter, onKeyDown]);

  // Initiales Paint.
  useEffect(() => {
    paint(value, null);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  return (
    <div
      ref={elRef}
      id={id}
      role="textbox"
      aria-multiline={multiline}
      aria-label={ariaLabel}
      aria-describedby={ariaDescribedBy}
      aria-invalid={ariaInvalid ? true : undefined}
      contentEditable={!disabled}
      suppressContentEditableWarning
      spellCheck
      data-placeholder={placeholder}
      data-empty={value.length === 0 ? 'true' : 'false'}
      style={rows ? { minHeight: `${rows * 1.5}em` } : undefined}
      className={cx(
        styles.field,
        multiline ? styles.multiline : wrapping ? styles.wrapping : styles.singleline,
        className,
      )}
      onInput={handleInput}
      onPaste={handlePaste}
      onKeyDown={handleKeyDown}
      onCompositionStart={() => handleComposition(false)}
      onCompositionEnd={() => handleComposition(true)}
      onFocus={onFocus}
      onBlur={onBlur}
    />
  );
});
